import type { FilterPreset, AdvancedFilterOptions } from '../../../shared/types';

const STORAGE_KEY = 'dombase_filter_presets';

export class FilterPresetManager {
  /**
   * Save a filter preset to localStorage
   */
  static savePreset(name: string, filters: AdvancedFilterOptions): FilterPreset {
    const preset: FilterPreset = {
      id: crypto.randomUUID(),
      name: name.trim(),
      filters: { ...filters },
      createdAt: new Date().toISOString()
    };

    const presets = this.loadPresets();
    presets.push(preset);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
      return preset;
    } catch (error) {
      console.error('Failed to save filter preset:', error);
      throw new Error('Failed to save filter preset. LocalStorage may be full.');
    }
  }

  /**
   * Load all filter presets from localStorage
   */
  static loadPresets(): FilterPreset[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];

      const presets = JSON.parse(stored) as FilterPreset[];

      // Validate and clean up presets
      return presets.filter(preset =>
        preset.id &&
        preset.name &&
        preset.filters &&
        preset.createdAt
      );
    } catch (error) {
      console.error('Failed to load filter presets:', error);
      return [];
    }
  }

  /**
   * Get a specific preset by ID
   */
  static getPreset(id: string): FilterPreset | null {
    const presets = this.loadPresets();
    return presets.find(preset => preset.id === id) || null;
  }

  /**
   * Update an existing preset
   */
  static updatePreset(id: string, updates: Partial<Pick<FilterPreset, 'name' | 'filters'>>): FilterPreset | null {
    const presets = this.loadPresets();
    const index = presets.findIndex(preset => preset.id === id);

    if (index === -1) return null;

    presets[index] = {
      ...presets[index],
      ...updates,
      id: presets[index].id, // Preserve ID
      createdAt: presets[index].createdAt // Preserve creation date
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
      return presets[index];
    } catch (error) {
      console.error('Failed to update filter preset:', error);
      throw new Error('Failed to update filter preset. LocalStorage may be full.');
    }
  }

  /**
   * Delete a preset by ID
   */
  static deletePreset(id: string): boolean {
    const presets = this.loadPresets();
    const filteredPresets = presets.filter(preset => preset.id !== id);

    if (filteredPresets.length === presets.length) {
      return false; // Preset not found
    }

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredPresets));
      return true;
    } catch (error) {
      console.error('Failed to delete filter preset:', error);
      throw new Error('Failed to delete filter preset.');
    }
  }

  /**
   * Apply a preset by ID, returning the filters
   */
  static applyPreset(id: string): AdvancedFilterOptions | null {
    const preset = this.getPreset(id);
    return preset ? { ...preset.filters } : null;
  }

  /**
   * Check if a preset name already exists
   */
  static isNameTaken(name: string, excludeId?: string): boolean {
    const presets = this.loadPresets();
    return presets.some(preset =>
      preset.name.toLowerCase() === name.toLowerCase() &&
      preset.id !== excludeId
    );
  }

  /**
   * Get preset count
   */
  static getPresetCount(): number {
    return this.loadPresets().length;
  }

  /**
   * Clear all presets
   */
  static clearAllPresets(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear filter presets:', error);
      throw new Error('Failed to clear filter presets.');
    }
  }

  /**
   * Export presets as JSON
   */
  static exportPresets(): string {
    const presets = this.loadPresets();
    return JSON.stringify(presets, null, 2);
  }

  /**
   * Import presets from JSON
   */
  static importPresets(jsonData: string): { success: number; errors: string[] } {
    try {
      const importedPresets = JSON.parse(jsonData) as FilterPreset[];

      if (!Array.isArray(importedPresets)) {
        throw new Error('Invalid preset data format');
      }

      const existingPresets = this.loadPresets();
      const errors: string[] = [];
      let successCount = 0;

      for (const preset of importedPresets) {
        try {
          // Validate preset structure
          if (!preset.id || !preset.name || !preset.filters || !preset.createdAt) {
            errors.push(`Invalid preset structure: ${preset.name || 'Unknown'}`);
            continue;
          }

          // Check for name conflicts
          if (this.isNameTaken(preset.name)) {
            preset.name = `${preset.name} (Imported)`;
          }

          // Generate new ID to avoid conflicts
          preset.id = crypto.randomUUID();
          preset.createdAt = new Date().toISOString();

          existingPresets.push(preset);
          successCount++;
        } catch (error) {
          errors.push(`Failed to import preset: ${preset.name || 'Unknown'}`);
        }
      }

      if (successCount > 0) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(existingPresets));
      }

      return { success: successCount, errors };
    } catch (error) {
      throw new Error('Invalid JSON data for presets import');
    }
  }
}

// Convenience functions for easier usage
export const saveFilterPreset = FilterPresetManager.savePreset.bind(FilterPresetManager);
export const loadFilterPresets = FilterPresetManager.loadPresets.bind(FilterPresetManager);
export const getFilterPreset = FilterPresetManager.getPreset.bind(FilterPresetManager);
export const updateFilterPreset = FilterPresetManager.updatePreset.bind(FilterPresetManager);
export const deleteFilterPreset = FilterPresetManager.deletePreset.bind(FilterPresetManager);
export const applyFilterPreset = FilterPresetManager.applyPreset.bind(FilterPresetManager);
export const isFilterPresetNameTaken = FilterPresetManager.isNameTaken.bind(FilterPresetManager);
export const getFilterPresetCount = FilterPresetManager.getPresetCount.bind(FilterPresetManager);
export const clearAllFilterPresets = FilterPresetManager.clearAllPresets.bind(FilterPresetManager);
export const exportFilterPresets = FilterPresetManager.exportPresets.bind(FilterPresetManager);
export const importFilterPresets = FilterPresetManager.importPresets.bind(FilterPresetManager);
